import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import FormErrors from "components/formParts/formErrors"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { useTranslation } from "react-i18next"
import { InputMaxLength, StorageWrite, StorageSettings, StoragePlan } from "types"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import ToggleInput from "components/inputs/toggleInput"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import Button from "components/inputs/button"
import Form from "components/forms/form"
import { logger } from "utils/secureLogger"
import ProcessingButton from "components/inputs/processingButton"
import useStorages from "hooks/useStorages"
import { setTargetStorage } from "@/utils/targetStorage"
import useLayout from "hooks/useLayout"
import useStorageLimits from "hooks/useStorageLimits"
import Callout from "components/callout/callout"

const CreateStorageForm = () => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { addToast } = useLayout()
    
    // Get all storages to count them
    const { storages } = useStorages()
    const currentStorageCount = storages?.length || 0

    // Input data.
    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [location, setLocation] = useState(``)
    const [storagePlan, setStoragePlan] = useState<StoragePlan>(StoragePlan.free)
    const [defaultProductUnit, setDefaultProductUnit] = useState(``)
    const [enableTransactions, setEnableTransactions] = useState(false)
    const [enableCustomersAndSuppliers, setEnableCustomersAndSuppliers] = useState(false)
    const [requireActionsLinkedToTransactions, setRequireActionsLinkedToTransactions] = useState(false)
    const [enableStockAdjustment, setEnableStockAdjustment] = useState(false)

    // Update feature toggles based on plan
    useEffect(() => {
        if (storagePlan === StoragePlan.free) {
            setEnableTransactions(false)
            setEnableCustomersAndSuppliers(false)
            setRequireActionsLinkedToTransactions(false)
        } else {
            setEnableTransactions(true)
            setEnableCustomersAndSuppliers(true)
            setRequireActionsLinkedToTransactions(true)
        }
    }, [storagePlan])

    // Get account limits based on selected plan
    const { canAddStorage, canUseCustomers, canUseSuppliers, canUseTransactions } = useStorageLimits({
        storagePlan,
        currentStorageCount
    })

    // Update feature toggles when plan changes
    useEffect(() => {
        // Disable features not supported by the plan
        if (!canUseTransactions) {
            setEnableTransactions(false)
            setRequireActionsLinkedToTransactions(false)
        }
        if (!canUseCustomers || !canUseSuppliers) {
            setEnableCustomersAndSuppliers(false)
        }
    }, [storagePlan, canUseTransactions, canUseCustomers, canUseSuppliers])

    // Auto-enable transactions if customers/suppliers is enabled
    useEffect(() => {
        if (enableCustomersAndSuppliers && !enableTransactions) {
            setEnableTransactions(true)
        }
    }, [enableCustomersAndSuppliers])
    useEffect(() => {
        if (!enableTransactions) {
            setEnableCustomersAndSuppliers(false)
            setRequireActionsLinkedToTransactions(false)
        }
    }, [enableTransactions])

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Storage mutations
    const { createStorageAsync, isCreating } = useStorages()

    // Validate the form.
    const validate = () => {
        let newErrors = [] as string[]
        if (name === '') {
            newErrors = [...newErrors, 'Name is required']
        }
        if (name.length < 3) {
            newErrors = [...newErrors, 'Name must be at least 3 characters']
        }
        setErrors(newErrors)
        return newErrors.length === 0
    }

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) return
        
        // Check storage limit before submitting
        if (!canAddStorage) {
            setErrors([t('errorStorageLimitReached')])
            addToast({ text: t('errorStorageLimitReached'), colorScheme: 'red' })
            return
        }
        
        try {
            const data: StorageWrite = {
                _version: 1,
                name,
                description,
                location,
                settings: {
                    storagePlan,
                    defaultProductUnit,
                    enableTransactions: enableTransactions === true && canUseTransactions,
                    enableCustomersAndSuppliers: enableCustomersAndSuppliers === true && canUseCustomers && canUseSuppliers,
                    requireActionsLinkedToTransactions: requireActionsLinkedToTransactions === true,
                    enableStockAdjustment: enableStockAdjustment === true
                } as StorageSettings
            }
            const newStorage = await createStorageAsync(data)
            logger.info('Storage creation successful', { storageId: newStorage.id }, 'CreateStorage')
            addToast({ text: t('toastStorageCreated'), colorScheme: 'green' })
            setTargetStorage(newStorage.id, name)
            navigate('/storage')
        } catch (error: unknown) {
            logger.error('Failed to create storage', error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastStorageError'), colorScheme: 'red' })
        }
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    // Handle location change.
    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocation(e.target.value)
    }

    // Handle default product unit change.
    const handleDefaultProductUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultProductUnit(e.target.value)
    }

    // Handle enable transactions change.
    const handleEnableTransactionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableTransactions(e.target.checked)
        if (e.target.checked) {
            setRequireActionsLinkedToTransactions(true)
        } else {
            setEnableCustomersAndSuppliers(false)
            setRequireActionsLinkedToTransactions(false)
        }
    }

    // Handle enable customers and suppliers change.
    const handleEnableCustomersAndSuppliersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableCustomersAndSuppliers(e.target.checked)
        if (e.target.checked) {
            setEnableTransactions(true)
        }
    }

    const handleRequireActionsLinkedToTransactionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRequireActionsLinkedToTransactions(e.target.checked)
        if (e.target.checked) {
            setEnableTransactions(true)
        }
    }

    const handleEnableStockAdjustmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableStockAdjustment(e.target.checked)
    }

    // Return the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isCreating}
        >
            {/* Section 1: Basic Information */}
            <Section padX={false}>
                {!canAddStorage && (
                    <Callout type="warning">
                        {t('errorStorageLimitReached')}
                    </Callout>
                )}
                <TextInput label={t('name')} value={name} onChange={handleNameChange} required maxLength={InputMaxLength.short} />
                <TextInput label={t('location')} value={location} onChange={handleLocationChange} maxLength={InputMaxLength.medium} />
                <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={InputMaxLength.long} />
                <TextInput label={t('defaultProductUnit')} value={defaultProductUnit} onChange={handleDefaultProductUnitChange} maxLength={InputMaxLength.short} />
            </Section>

            {/* Section 2: Plan Selection */}
            <Section design="card-grid-3">
                <Section
                    highlight={storagePlan === StoragePlan.free}
                    highlightable={true}
                    onClick={() => setStoragePlan(StoragePlan.free)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanFree')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanFreeDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanFreeProducts')}</li>
                        <li>✓ {t('storagePlanFreeCategories')}</li>
                        <li>✓ 5 custom inputs per category (text, number only)</li>
                    </ul>
                </Section>

                <Section
                    highlight={storagePlan === StoragePlan.pro}
                    highlightable={true}
                    onClick={() => setStoragePlan(StoragePlan.pro)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanPro')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanProDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanProProducts')}</li>
                        <li>✓ {t('storagePlanProCategories')}</li>
                        <li>✓ {t('storagePlanProTransactions')}</li>
                        <li>✓ {t('storagePlanProSharedAccess')}</li>
                        <li>✓ {t('storagePlanProCustomers')}</li>
                        <li>✓ {t('storagePlanProSuppliers')}</li>
                        <li>✓ 15 custom inputs per category (all types)</li>
                    </ul>
                </Section>

                <Section
                    highlight={storagePlan === StoragePlan.premium}
                    highlightable={true}
                    onClick={() => setStoragePlan(StoragePlan.premium)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanPremium')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanPremiumDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanPremiumProducts')}</li>
                        <li>✓ {t('storagePlanPremiumCategories')}</li>
                        <li>✓ {t('storagePlanPremiumTransactions')}</li>
                        <li>✓ {t('storagePlanPremiumSharedAccess')}</li>
                        <li>✓ {t('storagePlanPremiumCustomers')}</li>
                        <li>✓ {t('storagePlanPremiumSuppliers')}</li>
                        <li>✓ 40 custom inputs per category (all types)</li>
                    </ul>
                </Section>
            </Section>

            {/* Section 3: Feature Settings */}
            <Section padX={false}>
                <ToggleInput 
                    label={t('labelEnableStockAdjustment')} 
                    helperText={t('helperTextEnableStockAdjustment')} 
                    checked={enableStockAdjustment} 
                    onChange={handleEnableStockAdjustmentChange} 
                />
                {canUseTransactions && (
                    <>
                    <ToggleInput 
                        label={t('enableTransactions')} 
                        checked={enableTransactions} 
                        onChange={handleEnableTransactionsChange} 
                    />
                    <ToggleInput 
                        label={t('labelRequireActionsLinkedToTransactions')} 
                        helperText={t('helperTextRequireActionsLinkedToTransactions')} 
                        checked={requireActionsLinkedToTransactions} 
                        onChange={handleRequireActionsLinkedToTransactionsChange}
                        disabled={!enableTransactions}
                    />
                    </>
                )}
                
                {(canUseCustomers && canUseSuppliers) && (
                    <ToggleInput 
                        label={t('labelEnableCustomersAndSuppliers')} 
                        helperText={t('helperTextEnableCustomersAndSuppliers')} 
                        checked={enableCustomersAndSuppliers} 
                        onChange={handleEnableCustomersAndSuppliersChange} 
                    />
                )}
            </Section>

            <div className={`input-holder`}>
                {isCreating ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary" disabled={!canAddStorage}><AddIcon /><span>{t('labelCreateStorage')}</span></Button>
                )}
            </div>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    )
}

export default CreateStorageForm


